import { hasValidGs1CheckDigit } from './gs1';
import type { ScanResult } from './scanResult';

/**
 * Retail (linear) product codes — UPC-A, EAN-13, UPC-E, EAN-8 and a bare GTIN-14 — for the
 * Pick → Verify → Ship (Option 2) screens, plus the unit payload those screens send.
 *
 * WHY A HELPER OF ITS OWN rather than a change to parseScanPayload: a retail code has no AI 01, so
 * it never produces `gtin`, and three Option-1 screens (plus explainUnusableSerialScan) read
 * `gtin || sscc` as "that is a product, not a zone label". Teaching the parser to emit a GTIN for
 * bare digits would change what every one of them does with a UPC.
 *
 * RECOGNISED FROM `text`, NEVER FROM `kind`: 1.6–2.7% of valid retail codes happen to parse as a
 * GS1 element string (`304015088092` → (30)40 (15)088092) and can even surface a bogus serial,
 * lot or expiration. The digits are the only reliable signal.
 *
 * CANONICAL FORM = GTIN-14. Leading zeros carry no weight in the mod-10 check digit, so left-padding
 * a UPC-A / EAN-13 / EAN-8 to 14 digits keeps it valid and makes it comparable with a DataMatrix
 * `gtin` — and with the server, which normalises the same way (`toGtin14` in libs/services
 * pick-verify/product-code.ts; keep the two in step). For a US NDC pack `UPC-A = 3 + NDC10 + C`,
 * so its GTIN-14 is `003 + NDC10 + C` and its middle-12 is `03 + NDC10`.
 */

/** The PickVerifyUnitInput GraphQL input (Pick confirm, Verify scan / remove). */
export type PickVerifyUnitInput = {
  /** Normalized scan text (ScanResult.text) or a typed UPC / GTIN / NDC. */
  code?: string | null;
  gtin?: string | null;
  serialNumber?: string | null;
  lot?: string | null;
  /** YYYY-MM-DD */
  expiration?: string | null;
  manualEntry?: boolean | null;
  /**
   * Verify, bulk lines: units to add/remove (server default 1) — a typed entry's quantity, or what
   * the operator attributed to one lot in the lot chooser.
   */
  quantity?: number | null;
  /**
   * Verify, BULK / UPC lines only: the stock balance (zone + lot + expiration) to take the units
   * from (scan) or put them back to (remove) — a SalesOrderVerifyLot.balanceId. Left out, the server
   * picks (the Pick-confirmed lot, else FEFO; Remove: newest first). Ignored for SERIALIZED groups.
   */
  balanceId?: string | null;
};

/**
 * What the helpers below read off a scan. A full ScanResult satisfies it, and so do the ad-hoc
 * manual-entry objects SharedScanner hands to `handleScan` (`{ serialNumber, gtin, isManualEntry }`).
 */
export type ProductScanLike =
  | (Partial<Pick<ScanResult, 'gtin' | 'serialNumber' | 'lot' | 'expiration' | 'sscc' | 'text'>> & {
      isManualEntry?: boolean;
    })
  | null
  | undefined;

/**
 * A compressed 8-digit UPC-E (number system 0 or 1, six data digits, check digit) expanded to its
 * 12-digit UPC-A. The check digit is carried over unvalidated — it belongs to the UPC-A, so the
 * caller validates the expansion. Null for anything that is not shaped like a UPC-E.
 */
export const expandUpcE = (upce: string | null | undefined): string | null => {
  if (typeof upce !== 'string' || !/^[01]\d{7}$/.test(upce)) return null;
  const numberSystem = upce[0];
  const [d1, d2, d3, d4, d5, d6] = upce.slice(1, 7);
  const check = upce[7];
  let body: string;
  if (d6 === '0' || d6 === '1' || d6 === '2') body = `${d1}${d2}${d6}0000${d3}${d4}${d5}`;
  else if (d6 === '3') body = `${d1}${d2}${d3}00000${d4}${d5}`;
  else if (d6 === '4') body = `${d1}${d2}${d3}${d4}00000${d5}`;
  else body = `${d1}${d2}${d3}${d4}${d5}0000${d6}`;
  return `${numberSystem}${body}${check}`;
};

/**
 * The canonical GTIN-14 of a scanned or typed retail code, or null when `text` is not one.
 *
 * Digits only (after trimming) — a zone label such as "A-12" or "AISLE-12-B" is never a product —
 * of length 12 (UPC-A), 13 (EAN-13; also what Scandit returns for a UPC-A) or 14 (bare GTIN-14)
 * with a valid check digit, or 8: read as UPC-E first (expanded, then validated as the UPC-A) and
 * as EAN-8 only when it cannot be a valid UPC-E — UPC-E is the one US packs carry, and a code that
 * validates both ways is far more likely to be one. An all-zero code is never a real product.
 */
export const retailCodeGtin14 = (text: string | null | undefined): string | null => {
  const digits = typeof text === 'string' ? text.trim() : '';
  if (!/^\d+$/.test(digits) || /^0+$/.test(digits)) return null;
  if (digits.length === 8) {
    const upcA = expandUpcE(digits);
    if (upcA && hasValidGs1CheckDigit(upcA)) return upcA.padStart(14, '0');
    return hasValidGs1CheckDigit(digits) ? digits.padStart(14, '0') : null;
  }
  if (digits.length === 12 || digits.length === 13 || digits.length === 14) {
    return hasValidGs1CheckDigit(digits) ? digits.padStart(14, '0') : null;
  }
  return null;
};

/**
 * GTIN-14 digits 1–12 — the packaging-indicator- and check-digit-agnostic key the server matches
 * products on (`hierarchyItemGtinMiddle12`, SalesOrderVerifyLine.matchKeys). Null unless `gtin14`
 * is exactly 14 digits.
 */
export const gtinMiddle12 = (gtin14: string | null | undefined): string | null => {
  const digits = typeof gtin14 === 'string' ? gtin14.trim() : '';
  return /^\d{14}$/.test(digits) ? digits.slice(1, 13) : null;
};

/**
 * The middle-12 of whatever product a scan names: from its GS1 `gtin` (a typed 12/13-digit value in
 * the GTIN field is padded like a retail code), else from the retail digits of `text`, else null.
 */
export const scanGtinMiddle12 = (result: ProductScanLike): string | null => {
  if (!result) return null;
  if (result.gtin) {
    const fromGtin = gtinMiddle12(result.gtin) ?? gtinMiddle12(retailCodeGtin14(result.gtin));
    if (fromGtin) return fromGtin;
  }
  return gtinMiddle12(retailCodeGtin14(result.text));
};

/** A bare retail code: no GS1 GTIN or SSCC, but `text` is a valid UPC / EAN / GTIN-14. */
export const isRetailCodeScan = (result: ProductScanLike): boolean =>
  !!result && !result.gtin && !result.sscc && retailCodeGtin14(result.text) !== null;

/**
 * Whether a scan is a product (or case / pallet) barcode rather than a zone or tote label: a GS1
 * GTIN, an SSCC, or a retail code. The Pick step machine uses it both ways — "that is a product
 * barcode, scan the zone label first" in the zone step, a soft hint for anything else in the unit
 * step.
 */
export const looksLikeProductScan = (result: ProductScanLike): boolean =>
  !!result?.gtin || !!result?.sscc || retailCodeGtin14(result?.text) !== null;

/**
 * The PickVerifyUnitInput for a scan. The server resolves the product from these fields (gtin
 * first, then `code`), so the scan is passed through as read — except for a bare retail code, whose
 * GS1 mis-parse "serial" / "lot" / "expiration" (see the header) is dropped rather than sent to a
 * server that would take it at face value. Manual-entry objects keep their flag.
 */
export const toPickVerifyUnit = (result: ProductScanLike): PickVerifyUnitInput => {
  const retail = isRetailCodeScan(result);
  const unit: PickVerifyUnitInput = {
    code: result?.text || null,
    gtin: result?.gtin || null,
    serialNumber: retail ? null : result?.serialNumber || null,
    lot: retail ? null : result?.lot || null,
    expiration: retail ? null : result?.expiration || null
  };
  if (result?.isManualEntry === true) unit.manualEntry = true;
  return unit;
};

const isRealDate = (year: number, month: number, day: number): boolean => {
  if (month < 1 || month > 12 || day < 1) return false;
  return day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
};

const ymd = (year: number, month: number, day: number) =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

/**
 * A typed expiration as `YYYY-MM-DD`, or null when it is not a real calendar date. Accepts what the
 * app's date inputs produce: `YYYY-MM-DD` (native `<input type=date>`, optionally with a time part),
 * `M/D/YYYY` (MobileAwareDatePicker writes `toLocaleDateString()`), or a Date (its local calendar
 * day — a picker's Date is local midnight, and the UTC day would be yesterday west of Greenwich).
 */
export const normalizeExpirationInput = (
  value: string | Date | null | undefined
): string | null => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return ymd(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }
  if (typeof value !== 'string') return null;
  const text = value.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(text);
  if (iso) {
    const [year, month, day] = iso.slice(1).map(Number);
    return isRealDate(year, month, day) ? ymd(year, month, day) : null;
  }
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  if (us) {
    const [month, day, year] = us.slice(1).map(Number);
    return isRealDate(year, month, day) ? ymd(year, month, day) : null;
  }
  return null;
};

export type ManualPickVerifyUnitFields = {
  /** Typed NDC (any hyphenation) / UPC / GTIN. Passed through trimmed — the server normalises. */
  code?: string | null;
  expiration?: string | Date | null;
  serialNumber?: string | null;
  gtin?: string | null;
  quantity?: number | string | null;
};

const trimmedOrNull = (value: string | null | undefined): string | null => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
};

/**
 * The PickVerifyUnitInput for a manual entry (`manualEntry: true`), trimmed — typed values are not
 * normalised by the scan pipeline, and "  0123 " must not reach the server as a different code.
 * `quantity` is sent only as a whole number ≥ 1; anything else is left out (the server then counts
 * one unit), so the entry form must validate the number itself before calling this.
 */
export const manualPickVerifyUnit = ({
  code,
  expiration,
  serialNumber,
  gtin,
  quantity
}: ManualPickVerifyUnitFields): PickVerifyUnitInput => {
  const unit: PickVerifyUnitInput = {
    code: trimmedOrNull(code),
    gtin: trimmedOrNull(gtin),
    serialNumber: trimmedOrNull(serialNumber),
    expiration: normalizeExpirationInput(expiration),
    manualEntry: true
  };
  const count =
    typeof quantity === 'string' && quantity.trim() !== '' ? Number(quantity) : quantity;
  if (typeof count === 'number' && Number.isInteger(count) && count >= 1) unit.quantity = count;
  return unit;
};
